import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

const TITLES = ["Mr", "Mrs", "Ms", "Miss", "Dr"];
const COUNTRIES = [
  "United Kingdom", "Ireland", "United States", "Canada",
  "United Arab Emirates", "India", "Pakistan", "Australia",
  "Singapore", "Zimbabwe", "South Africa", "Other",
];
const CONSORTIUMS = [
  "Not part of a consortium",
  "Non Licensed",
  "Advantage Travel Partnership",
  "The Travel Network Group",
  "Hays Travel Independence Group",
  "Other",
];

const INITIAL = {
  company_name: "", website: "", title: "Mr",
  first_name: "", last_name: "", email: "",
  mobile_number: "", phone_number: "", fax_number: "",
  address_1: "", address_2: "", city: "",
  postal_code: "", county_state: "", country: "United Kingdom",
  iata_number: "", atol_number: "", abta_number: "",
  consortium: "Not part of a consortium",
  backup_contact_name: "", backup_contact_number: "",
  remarks: "", terms_accepted: false,
};

const REQUIRED_FIELDS = [
  "company_name", "first_name", "last_name", "email",
  "phone_number", "address_1", "city", "postal_code", "country",
];

const Label = ({ htmlFor, children, required }) => (
  <label htmlFor={htmlFor} className="block text-[11px] tracking-widest uppercase text-muted2 mb-1.5">
    {children} {required && <span className="text-terracotta">*</span>}
  </label>
);

const TextInput = ({ id, name, type = "text", value, onChange, required, autoComplete }) => (
  <input
    id={id}
    name={name}
    type={type}
    value={value}
    onChange={onChange}
    required={required}
    autoComplete={autoComplete}
    className="w-full px-4 py-2.5 bg-linen border border-ink/20 focus:border-terracotta focus:outline-none text-ink text-sm placeholder:text-muted2/60 transition-colors"
    data-testid={`agent-reg-${name}`}
  />
);

const SelectInput = ({ id, name, value, onChange, required, children }) => (
  <select
    id={id}
    name={name}
    value={value}
    onChange={onChange}
    required={required}
    className="w-full px-4 py-2.5 bg-linen border border-ink/20 focus:border-terracotta focus:outline-none text-ink text-sm transition-colors"
    data-testid={`agent-reg-${name}`}
  >
    {children}
  </select>
);

/** Lazy-load the reCAPTCHA v3 script once, globally. */
function loadRecaptchaScript(siteKey) {
  if (!siteKey || typeof window === "undefined") return Promise.resolve();
  if (window.__recaptchaLoadPromise) return window.__recaptchaLoadPromise;
  window.__recaptchaLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-recaptcha-v3]');
    if (existing) { existing.addEventListener("load", resolve); existing.addEventListener("error", reject); return; }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    s.async = true;
    s.defer = true;
    s.dataset.recaptchaV3 = "true";
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.__recaptchaLoadPromise;
}

export default function TravelAgentRegistrationForm({ compact = false }) {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // {reference, message}

  // Preload reCAPTCHA v3 on mount so token retrieval is instant
  useEffect(() => {
    if (RECAPTCHA_SITE_KEY) {
      loadRecaptchaScript(RECAPTCHA_SITE_KEY).catch(() => {
        // reCAPTCHA preload failed — token retrieval will retry on submit
      });
    }
  }, []);

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    for (const f of REQUIRED_FIELDS) {
      if (!String(form[f] || "").trim()) return `Please fill in ${f.replace(/_/g, " ")}.`;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email address.";
    if (!form.terms_accepted) return "Please accept the terms & conditions to continue.";
    return null;
  };

  const getCaptchaToken = useCallback(async () => {
    if (!RECAPTCHA_SITE_KEY) return "";
    await loadRecaptchaScript(RECAPTCHA_SITE_KEY);
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha || !window.grecaptcha.ready) {
        return reject(new Error("reCAPTCHA not ready"));
      }
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action: "agent_register" })
          .then(resolve)
          .catch(reject);
      });
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const token = await getCaptchaToken();
      const res = await fetch(`${API_BASE}/api/travel-agents/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, recaptcha_token: token || "" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Registration failed");
      setSuccess(data);
      toast.success("Registration submitted");
    } catch (e2) {
      toast.error(e2.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        className="border border-ink/15 bg-sand p-8 md:p-12 text-center"
        data-testid="agent-reg-success"
      >
        <CheckCircle2 className="w-12 h-12 stroke-1 text-terracotta mx-auto" aria-hidden="true" />
        <h3 className="font-serif text-3xl mt-4 text-ink">Registration received</h3>
        <p className="mt-3 text-sm text-ink/80 max-w-md mx-auto leading-relaxed">
          {success.message}
        </p>
        <div className="mt-6 inline-block px-4 py-2 bg-linen border border-terracotta/40 font-mono text-sm text-terracotta">
          Reference: {success.reference}
        </div>
        <div className="mt-8">
          <button
            type="button"
            onClick={() => { setSuccess(null); setForm(INITIAL); }}
            className="text-sm text-terracotta hover:underline"
            data-testid="agent-reg-another"
          >
            Submit another registration
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-10" data-testid="agent-reg-form" noValidate>
      {/* ------ SECTION 01: Company ------ */}
      <fieldset className="space-y-5">
        <legend className="w-full">
          <div className="overline text-terracotta">01 · Company information</div>
          <p className="mt-1 text-xs text-muted2 italic">Fields marked * are mandatory.</p>
        </legend>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="company_name" required>Company name</Label>
            <TextInput id="company_name" name="company_name" value={form.company_name} onChange={onChange} required autoComplete="organization" />
          </div>
          <div>
            <Label htmlFor="website">Website</Label>
            <TextInput id="website" name="website" type="url" value={form.website} onChange={onChange} autoComplete="url" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div>
            <Label htmlFor="title">Title</Label>
            <SelectInput id="title" name="title" value={form.title} onChange={onChange}>
              {TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
            </SelectInput>
          </div>
          <div className="md:col-span-1">
            <Label htmlFor="first_name" required>First name</Label>
            <TextInput id="first_name" name="first_name" value={form.first_name} onChange={onChange} required autoComplete="given-name" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="last_name" required>Last name</Label>
            <TextInput id="last_name" name="last_name" value={form.last_name} onChange={onChange} required autoComplete="family-name" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="email" required>Email address</Label>
            <TextInput id="email" name="email" type="email" value={form.email} onChange={onChange} required autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="phone_number" required>Phone number</Label>
            <TextInput id="phone_number" name="phone_number" type="tel" value={form.phone_number} onChange={onChange} required autoComplete="tel" />
          </div>
          <div>
            <Label htmlFor="mobile_number">Mobile number</Label>
            <TextInput id="mobile_number" name="mobile_number" type="tel" value={form.mobile_number} onChange={onChange} autoComplete="tel" />
          </div>
          <div>
            <Label htmlFor="fax_number">Fax number</Label>
            <TextInput id="fax_number" name="fax_number" type="tel" value={form.fax_number} onChange={onChange} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5">
          <div>
            <Label htmlFor="address_1" required>Address 1</Label>
            <TextInput id="address_1" name="address_1" value={form.address_1} onChange={onChange} required autoComplete="address-line1" />
          </div>
          <div>
            <Label htmlFor="address_2">Address 2</Label>
            <TextInput id="address_2" name="address_2" value={form.address_2} onChange={onChange} autoComplete="address-line2" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <Label htmlFor="city" required>City</Label>
            <TextInput id="city" name="city" value={form.city} onChange={onChange} required autoComplete="address-level2" />
          </div>
          <div>
            <Label htmlFor="postal_code" required>Postal code</Label>
            <TextInput id="postal_code" name="postal_code" value={form.postal_code} onChange={onChange} required autoComplete="postal-code" />
          </div>
          <div>
            <Label htmlFor="county_state">County / State</Label>
            <TextInput id="county_state" name="county_state" value={form.county_state} onChange={onChange} autoComplete="address-level1" />
          </div>
        </div>

        <div>
          <Label htmlFor="country" required>Country</Label>
          <SelectInput id="country" name="country" value={form.country} onChange={onChange} required>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </SelectInput>
        </div>
      </fieldset>

      {/* ------ SECTION 02: Credentials ------ */}
      <fieldset className="space-y-5 pt-2 border-t border-ink/10">
        <legend className="w-full pt-6">
          <div className="overline text-terracotta">02 · Credentials &amp; verification</div>
        </legend>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <Label htmlFor="iata_number">IATA number</Label>
            <TextInput id="iata_number" name="iata_number" value={form.iata_number} onChange={onChange} />
          </div>
          <div>
            <Label htmlFor="atol_number">ATOL number</Label>
            <TextInput id="atol_number" name="atol_number" value={form.atol_number} onChange={onChange} />
          </div>
          <div>
            <Label htmlFor="abta_number">ABTA number</Label>
            <TextInput id="abta_number" name="abta_number" value={form.abta_number} onChange={onChange} />
          </div>
        </div>

        <div>
          <Label htmlFor="consortium">Part of any consortium?</Label>
          <SelectInput id="consortium" name="consortium" value={form.consortium} onChange={onChange}>
            {CONSORTIUMS.map((c) => <option key={c} value={c}>{c}</option>)}
          </SelectInput>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="backup_contact_name">Backup contact name</Label>
            <TextInput id="backup_contact_name" name="backup_contact_name" value={form.backup_contact_name} onChange={onChange} />
          </div>
          <div>
            <Label htmlFor="backup_contact_number">Backup contact number</Label>
            <TextInput id="backup_contact_number" name="backup_contact_number" type="tel" value={form.backup_contact_number} onChange={onChange} />
          </div>
        </div>

        <div>
          <Label htmlFor="remarks">Remarks</Label>
          <textarea
            id="remarks"
            name="remarks"
            rows={4}
            value={form.remarks}
            onChange={onChange}
            className="w-full px-4 py-2.5 bg-linen border border-ink/20 focus:border-terracotta focus:outline-none text-ink text-sm placeholder:text-muted2/60 transition-colors resize-y"
            data-testid="agent-reg-remarks"
          />
        </div>
      </fieldset>

      {/* ------ TERMS + CAPTCHA + SUBMIT ------ */}
      <fieldset className="space-y-6 pt-2 border-t border-ink/10">
        <legend className="pt-6 overline text-terracotta">03 · Confirm &amp; submit</legend>

        <label className="flex gap-3 items-start text-sm text-ink/80 cursor-pointer">
          <input
            type="checkbox"
            name="terms_accepted"
            checked={form.terms_accepted}
            onChange={onChange}
            required
            className="mt-1 accent-terracotta"
            data-testid="agent-reg-terms"
          />
          <span>
            By ticking this box I confirm the information provided is accurate and I accept the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline">Terms &amp; Conditions</a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline">Privacy Notice</a>{" "}
            of Simba Travel.
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="btn-primary w-full md:w-auto justify-center min-w-[220px] disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="agent-reg-submit"
        >
          {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>) : "Submit registration"}
        </button>

        {RECAPTCHA_SITE_KEY && (
          <div className="flex items-center gap-2 text-[11px] text-muted2" data-testid="agent-reg-captcha">
            <ShieldCheck className="w-3.5 h-3.5 stroke-1 text-terracotta flex-shrink-0" aria-hidden="true" />
            <span>
              Protected by Google reCAPTCHA — this site is protected against spam and abuse.
              See Google's{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-terracotta">Privacy Policy</a>{" "}
              and{" "}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-terracotta">Terms of Service</a>.
            </span>
          </div>
        )}

        {!compact && (
          <p className="text-[11px] text-muted2 italic max-w-2xl leading-relaxed">
            Registrations are reviewed by our travel-agent support desk. We aim to respond within
            72 hours. For urgent queries call{" "}
            <a href="tel:+442030237704" className="text-terracotta hover:underline">+44 20 3023 7704</a>
            {" "}or email{" "}
            <a href="mailto:support@simbatravel.co.uk" className="text-terracotta hover:underline">support@simbatravel.co.uk</a>.
          </p>
        )}
      </fieldset>
    </form>
  );
}
